// src/pages/DataTeam/DataTeamCadCentreDownload.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TitleWithBack from '../../components/common/TitleWithBack';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import PaginatedTable from '../../components/PaginatedTable';
import { useDialog } from '../../context/DialogContext';

// CAD "browse per exam centre": one row per (exam centre, module, series),
// each downloadable as the single "Candidates Work Bundle" zip the backend
// builds when the examiner marks upload as complete - the individual
// candidate folders bundled together for that centre, not re-zipped again
// here. For browsing/downloading a single candidate's own folder instead,
// see the separate "Browse per Candidate" screen.
export default function DataTeamCadCentreDownload() {
  const { alert } = useDialog();
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [seriesList, setSeriesList] = useState([]);
  const [seriesName, setSeriesName] = useState('');
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState({
    module: '',
    moduleCode: '',
    examCenterNumber: '',
    examCenterName: '',
    year: '',
    semester: ''
  });
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    axios.get('/api/exam-series')
      .then(res => setSeriesList(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`/api/exam-series/${seriesId}`)
      .then((res) => setSeriesName(res.data.name || `Series ${seriesId}`))
      .catch(() => {
        setError('Error fetching series data.');
        setLoading(false);
      });

    axios
      .get(`/api/exam-series/${seriesId}/assignments-with-uploads`, { params: { examType: 'CAD' } })
      .then((res) => {
        setTableData(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching assignments data.');
        setLoading(false);
      });
  }, [seriesId]);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const filteredData = tableData.filter((r) => {
    if (r.status !== 'COMPLETED') return false;
    if (filters.module && !r.moduleName.toLowerCase().includes(filters.module.toLowerCase())) return false;
    if (filters.moduleCode && !r.moduleCode.toLowerCase().includes(filters.moduleCode.toLowerCase())) return false;
    if (filters.examCenterNumber && !r.examCentreNumber.toLowerCase().includes(filters.examCenterNumber.toLowerCase())) return false;
    if (filters.examCenterName && !r.examCentreName.toLowerCase().includes(filters.examCenterName.toLowerCase())) return false;
    if (filters.year && !r.year.toLowerCase().includes(filters.year.toLowerCase())) return false;
    if (filters.semester && !r.semester.toLowerCase().includes(filters.semester.toLowerCase())) return false;
    return true;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortOrder('asc');
    }
  };

  const handleRowSelect = (row) => {
    setSelectedRows((prev) => {
      const exists = prev.find(r => r.assignmentId === row.assignmentId);
      return exists ? prev.filter(r => r.assignmentId !== row.assignmentId) : [...prev, row];
    });
  };
  const isRowSelected = (row) => selectedRows.some(r => r.assignmentId === row.assignmentId);

  const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '');
  const findBundleDoc = (row) => (row.documents || []).find(d => d.documentType === 'Candidates Work Bundle');
  const findReportDoc = (row) => (row.documents || []).find(d => d.documentType === 'Report');

  // Report is written by the examiner in-browser and saved straight to PDF -
  // Data Team/Security can download it directly; examiners only get
  // read-only preview (see ReportEditor.js), never a download of any kind.
  const handleDownloadReport = async (row) => {
    try {
      const res = await axios.get(`/api/assignments/${row.assignmentId}/report/download`, { responseType: 'blob' });
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_report.pdf`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No report available for this centre yet.', { title: 'Unavailable', variant: 'warning' });
    }
  };

  const handleDownloadBundle = async (row) => {
    const doc = findBundleDoc(row);
    if (!doc) {
      alert('No candidates work bundle available for this centre yet - it is created automatically once the examiner marks the upload complete and the archiver approves it.', { title: 'Unavailable', variant: 'warning' });
      return;
    }
    try {
      const res = await axios.get(`/api/archiver/reports/students-work/download/${doc.documentId}`, { responseType: 'blob' });
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_candidates_work_bundle.zip`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('Failed to download the candidates work bundle.', { title: 'Error', variant: 'danger' });
    }
  };

  // Same two-level zip-of-zips mechanism used elsewhere: one inner zip per
  // exam centre (containing that centre's already-zipped documents),
  // bundled into one outer zip for the whole selection.
  const handleDownloadSelectedZip = async () => {
    if (!selectedRows.length) return;
    try {
      const assignmentIdsQuery = selectedRows.map(r => `assignmentIds=${r.assignmentId}`).join('&');
      const res = await axios.get(
        `/api/exam-series/${seriesId}/download-zip-filtered?${assignmentIdsQuery}`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(seriesName) || 'series'}_cad_practical_work.zip`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('Failed to download zip for the selected exam centres.', { title: 'Error', variant: 'danger' });
    }
  };

  const columns = [
    { key: 'select', header: '' },
    { key: 'serial', header: <>#</> },
    { key: 'examCentreNumber', header: <span onClick={() => handleSort('examCentreNumber')} style={{cursor: 'pointer'}}>Center no {sortColumn === 'examCentreNumber' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'examCentreName', header: <span onClick={() => handleSort('examCentreName')} style={{cursor: 'pointer'}}>Center Name {sortColumn === 'examCentreName' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'moduleCode', header: <span onClick={() => handleSort('moduleCode')} style={{cursor: 'pointer'}}>Module Code {sortColumn === 'moduleCode' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'moduleName', header: <span onClick={() => handleSort('moduleName')} style={{cursor: 'pointer'}}>Module Name {sortColumn === 'moduleName' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'seriesName', header: <span onClick={() => handleSort('seriesName')} style={{cursor: 'pointer'}}>Series {sortColumn === 'seriesName' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'year', header: <span onClick={() => handleSort('year')} style={{cursor: 'pointer'}}>Year {sortColumn === 'year' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'semester', header: <span onClick={() => handleSort('semester')} style={{cursor: 'pointer'}}>Semester {sortColumn === 'semester' && (sortOrder === 'asc' ? '▲' : '▼')}</span> },
    { key: 'candidateCount', header: 'Candidate Folders' },
    { key: 'status', header: 'Status' },
    { key: 'download', header: 'Download' }
  ];

  const renderRow = (r, idx, pageIndex, pageSize) => {
    const bundleDoc = findBundleDoc(r);
    const reportDoc = findReportDoc(r);
    const candidateCount = (r.documents || []).filter(d => d.documentType?.startsWith('Student_') && d.documentType?.endsWith('_Work')).length;
    return (
      <>
        <td onClick={e => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={isRowSelected(r)}
            onChange={() => handleRowSelect(r)}
            aria-label={`Select row ${(pageIndex * pageSize) + idx + 1}`}
          />
        </td>
        <td>{(pageIndex * pageSize) + idx + 1}</td>
        <td><small>{r.examCentreNumber}</small></td>
        <td><small>{r.examCentreName}</small></td>
        <td><small>{r.moduleCode}</small></td>
        <td><small>{r.moduleName}</small></td>
        <td><small>{r.seriesName}</small></td>
        <td><small>{r.year}</small></td>
        <td><small>{r.semester}</small></td>
        <td><small>{candidateCount}</small></td>
        <td>
          <small>
            <Badge bg={r.status === 'COMPLETED' ? 'success' : 'secondary'}>{r.status}</Badge>
          </small>
        </td>
        <td onClick={e => e.stopPropagation()}>
          <div className="d-flex flex-column gap-1">
            <Button size="sm" variant="outline-primary" onClick={() => handleDownloadBundle(r)} disabled={!bundleDoc}>
              Candidates Work Bundle (.zip)
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => handleDownloadReport(r)} disabled={!reportDoc}>
              Report (.pdf)
            </Button>
          </div>
        </td>
      </>
    );
  };

  if (loading) return <div className="mt-4 container">Loading...</div>;
  if (error) return <div className="mt-4 container text-danger">{error}</div>;

  return (
    <Container className="mt-2" fluid>
      <TitleWithBack title={`CAD practical work - browse per exam centre (${seriesName})`} />
      <Row className="mb-3">
        <Col md={4} className="mb-2">
          <Form.Label>Select Series</Form.Label>
          <Form.Select
            value={seriesId}
            onChange={e => navigate(`/data-team/cad/centres/${e.target.value}`)}
          >
            {seriesList.map(series => (
              <option key={series.id} value={series.id}>
                {series.name} ({series.startDate} to {series.endDate})
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={2}>
          <Form.Label>Center no</Form.Label>
          <Form.Control type="text" name="examCenterNumber" value={filters.examCenterNumber} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Center Name</Form.Label>
          <Form.Control type="text" name="examCenterName" value={filters.examCenterName} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Module Code</Form.Label>
          <Form.Control type="text" name="moduleCode" value={filters.moduleCode} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Module name</Form.Label>
          <Form.Control type="text" name="module" value={filters.module} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Year</Form.Label>
          <Form.Control type="text" name="year" value={filters.year} onChange={handleFilterChange} />
        </Col>
        <Col md={2}>
          <Form.Label>Semester</Form.Label>
          <Form.Control type="text" name="semester" value={filters.semester} onChange={handleFilterChange} />
        </Col>
      </Row>
      {sortedData.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedData}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      <div className="text-end mt-3">
        <Button variant="primary" onClick={handleDownloadSelectedZip} disabled={!selectedRows.length}>
          Download zip (selected centres)
        </Button>
      </div>
    </Container>
  );
}
